import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";
import { v4 as uuidv4 } from 'uuid';
import { WS_URL } from "../config";

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

/**
 * museumx backend (see docs/API_SPEC.md):
 *   server -> client : sessionState, sessionStatus, sessionHardEnded,
 *                      registered, reconnected, profileUpdated,
 *                      score, combo, beat, icon, rank, team, color,
 *                      clientCountUpdate
 *   client -> server : getSessionStatus, register, userReconnect,
 *                      image, val, icon_speed
 *
 * Statuses: READY | CONTENT1 | CONTENT2 | CONTENTS_ENDED | ENDED
 *
 * Identity: a per-device UUID in localStorage ("museumx_user_id"). Two
 * browsers => two users, even on localhost. No ?session= URL token.
 *
 * Persistence: nickname, profile index, and the profile image (base64) are
 * cached in localStorage so a returning user keeps their info — and are
 * wiped when the session ends (sessionHardEnded / stale reconnect).
 *
 * Keep-alive: ported verbatim from mobile-sensors —
 *   - screen wake lock (utils/wakeLock.js, started in App.js)
 *   - aggressive socket recovery: forceReconnectNow() on tab focus,
 *     network back, and on any tap while disconnected.
 */

// localStorage keys wiped when a registration goes stale / session ends.
const SESSION_KEYS = [
  "museumx_nickname",
  "museumx_db_user_id",
  "museumx_created_at",
  "museumx_profile_index",
  "museumx_profile_image",
];

function wipeSessionKeys() {
  SESSION_KEYS.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
  try { sessionStorage.removeItem("museumx_started"); } catch {}
}

// Hard-end also revokes the QR gate token so a refresh re-validates against
// the next active session — without a fresh QR scan, the Gate sends the
// user back to EmptyPage.
function revokeQrSessionToken() {
  try { sessionStorage.removeItem("museumx_session_token"); } catch {}
  try { localStorage.removeItem("museumx_last_session_uuid"); } catch {}
}

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [sessionStatus, setSessionStatus] = useState(null);

  // Sticky terminal flag: once the current session is hard-ended, lock the
  // app on EmptyPage until the user reloads the page. Without this, a NEW
  // StartSession arriving on the same open tab would flip status back to
  // READY and send the user straight to Landing/Nickname even though they
  // never re-scanned the QR. The reload forces the Gate (index.js) to
  // re-validate the URL ?session=<uuid> token against the new session UUID.
  const [sessionTerminated, setSessionTerminated] = useState(false);

  // True while we verify a returning user's registration with the server.
  const [bootstrapping, setBootstrapping] = useState(() => {
    try { return !!localStorage.getItem("museumx_nickname"); } catch { return false; }
  });

  const [userInfo, setUserInfo] = useState({
    userId: null,
    nickname: null,
    clientIndex: null,
    dbUserId: null,
    profileIndex: null,
    profileImage: null, // base64 data URL of the chosen profile image
  });

  // Live game state — kept in context so it survives screen changes
  // (the `team` OSC arrives ~3s before CONTENT2 / before Wire mounts).
  const [team, setTeam] = useState(null);            // 0=red, 1=green
  const [score, setScore] = useState({ total: 0, maxCombo: 0 });
  const [rank, setRank] = useState(null);
  const [combo, setCombo] = useState(0);

  // Restore device identity + cached profile from localStorage.
  useEffect(() => {
    let uid;
    try { uid = localStorage.getItem("museumx_user_id"); } catch {}
    if (!uid) {
      uid = uuidv4();
      try { localStorage.setItem("museumx_user_id", uid); } catch {}
    }
    let pi = null, nick = null, img = null;
    try { pi = Number(localStorage.getItem("museumx_profile_index")); } catch {}
    try { nick = localStorage.getItem("museumx_nickname"); } catch {}
    try { img = localStorage.getItem("museumx_profile_image"); } catch {}

    setUserInfo((prev) => ({
      ...prev,
      userId: uid,
      nickname: nick || null,
      profileIndex: Number.isFinite(pi) ? pi : null,
      profileImage: img || null,
    }));
  }, []);

  const socketRef = useRef(null);

  useEffect(() => {
    if (socketRef.current) return;

    console.log("[CTX] Connecting to:", WS_URL);
    const newSocket = io(WS_URL, {
      path: "/socket.io/",
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 4000,
      randomizationFactor: 0.5,
      timeout: 10000,
      forceNew: true,
    });
    socketRef.current = newSocket;
    setSocket(newSocket);

    // ---- bootstrap gate -------------------------------------------------
    let bootstrapDone = false;
    const finishBootstrap = () => {
      if (bootstrapDone) return;
      bootstrapDone = true;
      setBootstrapping(false);
    };
    const bootstrapTimer = setTimeout(finishBootstrap, 4000);

    // ---- helpers --------------------------------------------------------
    const requestStatusSync = () => {
      try { newSocket.emit("getSessionStatus"); } catch {}
    };

    const resetGameState = () => {
      setTeam(null);
      setScore({ total: 0, maxCombo: 0 });
      setRank(null);
      setCombo(0);
    };

    // status polling while the session is inactive (catches StartSession fast)
    let pollTimer = null;
    const startPoll = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => {
        if (newSocket.connected) requestStatusSync();
      }, 1000);
    };
    const stopPoll = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    let prevStatus = null;
    const onStatus = (raw) => {
      const s = String(raw || "").toUpperCase();
      if (!s) return;
      setSessionStatus(s);
      // A fresh READY means a (possibly NEW) session. Re-validate our saved
      // registration against it via tryRejoin -> userReconnect: the backend
      // answers `welcomeBack:false` when the registration belongs to an old
      // session, which wipes it — so a new StartSession can't skip the
      // landing/nickname screen with a stale registration.
      if (s === "READY" && prevStatus !== "READY") {
        tryRejoin();
      }
      prevStatus = s;
      if (s === "READY" || s === "CONTENT1" || s === "CONTENT2" || s === "CONTENTS_ENDED") {
        stopPoll();
      } else {
        startPoll();
      }
    };

    // ---- rejoin (returning user) ---------------------------------------
    let rejoinAck = false;
    let rejoinTimer = null;
    let rejoinTries = 0;
    const clearRejoin = () => {
      if (rejoinTimer) { clearInterval(rejoinTimer); rejoinTimer = null; }
      rejoinTries = 0;
    };
    const tryRejoin = () => {
      let nick = null, uid = null;
      try {
        nick = localStorage.getItem("museumx_nickname");
        uid = localStorage.getItem("museumx_user_id");
      } catch {}
      if (!nick || !uid) { finishBootstrap(); return; }

      rejoinAck = false;
      clearRejoin();
      newSocket.emit("userReconnect", { userId: uid });
      rejoinTimer = setInterval(() => {
        if (!newSocket.connected) return;
        if (rejoinAck) { clearRejoin(); return; }
        rejoinTries += 1;
        if (rejoinTries > 6) { clearRejoin(); finishBootstrap(); return; }
        newSocket.emit("userReconnect", { userId: uid });
      }, 600);
    };

    // ---- keep-alive: aggressive socket recovery (ported from mobile-sensors)
    const forceReconnectNow = (reason = "manual") => {
      try {
        // Browser says it's offline — don't spam connect().
        if (typeof navigator !== "undefined" && navigator.onLine === false) return;

        // Already connected — just resync state + rejoin mapping.
        if (newSocket.connected) {
          requestStatusSync();
          tryRejoin();
          return;
        }

        console.warn("[CTX] forceReconnectNow:", reason);
        newSocket.connect(); // safe even if already connecting

        setTimeout(() => {
          if (newSocket.connected) {
            requestStatusSync();
            tryRejoin();
          }
        }, 200);
      } catch {}
    };

    // ---- lifecycle ------------------------------------------------------
    newSocket.on("connect", () => {
      console.log("[CTX] connected", newSocket.id);
      setIsConnected(true);
      requestStatusSync();
      startPoll();
      tryRejoin();
    });

    newSocket.on("disconnect", () => {
      console.warn("[CTX] disconnected");
      setIsConnected(false);
    });

    newSocket.on("connect_error", (err) => {
      console.warn("[CTX] connect_error:", err?.message || err);
      setIsConnected(false);
    });

    newSocket.io.on("reconnect", () => {
      requestStatusSync();
      tryRejoin();
    });

    // ---- session status -------------------------------------------------
    newSocket.on("sessionState", (d) => { if (d?.status) onStatus(d.status); });
    newSocket.on("sessionStatus", (s) => { if (s) onStatus(s); });

    // ---- hard end (/api/EndSession): wipe the saved registration --------
    newSocket.on("sessionHardEnded", () => {
      console.warn("[CTX] sessionHardEnded — wiping saved user info");
      resetGameState();
      wipeSessionKeys();
      revokeQrSessionToken();
      // Lock the app on EmptyPage until the page is reloaded. Even if a
      // brand-new session starts on this open tab, the user must reload
      // and re-validate via QR (Gate) — they cannot silently jump into
      // the next session.
      setSessionTerminated(true);
      setSessionStatus("ENDED");
      setUserInfo((prev) => ({
        ...prev,
        nickname: null, clientIndex: null, dbUserId: null,
        profileIndex: null, profileImage: null,
      }));
    });

    // ---- registration ---------------------------------------------------
    newSocket.on("registered", (d) => {
      console.log("[CTX] registered", d);
      finishBootstrap();
      if (d?.nickname) {
        try { localStorage.setItem("museumx_nickname", d.nickname); } catch {}
      }
      if (Number.isFinite(d?.dbUserId)) {
        try { localStorage.setItem("museumx_db_user_id", String(d.dbUserId)); } catch {}
      }
      setUserInfo((prev) => ({
        ...prev,
        nickname: d?.nickname ?? prev.nickname,
        clientIndex: d?.clientIndex ?? prev.clientIndex,
        dbUserId: Number.isFinite(d?.dbUserId) ? d.dbUserId : prev.dbUserId,
      }));
      if (d?.sessionStatus) setSessionStatus(String(d.sessionStatus).toUpperCase());
    });

    newSocket.on("reconnected", (d) => {
      console.log("[CTX] reconnected", d);
      rejoinAck = true;
      clearRejoin();
      finishBootstrap();

      if (d?.welcomeBack) {
        if (d?.nickname) {
          try { localStorage.setItem("museumx_nickname", d.nickname); } catch {}
        }
        if (Number.isFinite(d?.dbUserId)) {
          try { localStorage.setItem("museumx_db_user_id", String(d.dbUserId)); } catch {}
        }
        setUserInfo((prev) => ({
          ...prev,
          nickname: d?.nickname ?? prev.nickname,
          clientIndex: d?.clientIndex ?? prev.clientIndex,
          dbUserId: Number.isFinite(d?.dbUserId) ? d.dbUserId : prev.dbUserId,
        }));
        if (d?.sessionStatus) setSessionStatus(String(d.sessionStatus).toUpperCase());
      } else {
        // Saved registration belongs to an old session — drop it.
        wipeSessionKeys();
        setUserInfo((prev) => ({
          ...prev,
          nickname: null, clientIndex: null, dbUserId: null,
          profileIndex: null, profileImage: null,
        }));
      }
    });

    newSocket.on("profileUpdated", (d) => {
      console.log("[CTX] profileUpdated:", d?.success ? "ok" : "fail");
    });

    // ---- live game events ----------------------------------------------
    newSocket.on("team", (d) => {
      if (d && (d.team === 0 || d.team === 1)) setTeam(d.team);
    });
    newSocket.on("score", (d) => {
      if (!d) return;
      setScore({
        total: Number(d.total) || 0,
        maxCombo: Number(d.maxCombo) || 0,
      });
    });
    newSocket.on("rank", (d) => {
      if (d && Number.isFinite(d.rank)) setRank(d.rank);
    });
    newSocket.on("combo", (d) => {
      if (d && Number.isFinite(d.combo)) setCombo(d.combo);
    });

    // ---- keep-alive listeners ------------------------------------------
    const onVis = () => {
      if (document.visibilityState === "visible") forceReconnectNow("visibilitychange");
    };
    const onOnline = () => forceReconnectNow("online");
    const onOffline = () => {
      console.warn("[CTX] browser offline");
      setIsConnected(false);
    };
    // Any tap while disconnected -> attempt recovery immediately.
    const onAnyGesture = () => {
      if (!newSocket.connected) forceReconnectNow("gesture");
    };

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("pointerdown", onAnyGesture, true);

    return () => {
      clearTimeout(bootstrapTimer);
      stopPoll();
      clearRejoin();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("pointerdown", onAnyGesture, true);
      if (socketRef.current) socketRef.current.disconnect();
      socketRef.current = null;
    };
  }, []);

  /**
   * Register the device for the current session.
   *   nickname      — display name (server caps at 10 graphemes)
   *   profileImage  — chosen image index 0~49
   *   profileBase64 — (optional) base64 data URL of that image, persisted
   *                   to localStorage so the user's info survives a restart
   */
  const registerUser = (nickname, profileImage, profileBase64) => {
    const s = socketRef.current;
    if (!s) return;

    let uid;
    try { uid = localStorage.getItem("museumx_user_id"); } catch {}
    if (!uid) {
      uid = uuidv4();
      try { localStorage.setItem("museumx_user_id", uid); } catch {}
    }

    const raw = Math.floor(Number(profileImage));
    const pidx = Number.isFinite(raw) ? Math.max(0, Math.min(49, raw)) : 0;

    try {
      localStorage.setItem("museumx_profile_index", String(pidx));
      if (nickname) localStorage.setItem("museumx_nickname", nickname);
      if (profileBase64) localStorage.setItem("museumx_profile_image", profileBase64);
      if (!localStorage.getItem("museumx_created_at")) {
        localStorage.setItem("museumx_created_at", String(Date.now()));
      }
    } catch {}

    setUserInfo((prev) => ({
      ...prev,
      profileIndex: pidx,
      profileImage: profileBase64 || prev.profileImage,
    }));

    // register carries profileImage; the backend also fires OSC imageUpdated.
    s.emit("register", { userId: uid, nickname, profileImage: pidx });
  };

  // Edge-triggered tap for the tug-of-war (CONTENT2). 1 = press, 0 = release.
  const sendTap = (tap) => {
    const s = socketRef.current;
    if (!s) return;
    let uid;
    try { uid = localStorage.getItem("museumx_user_id"); } catch {}
    if (!uid) return;
    s.emit("val", { userId: uid, tap: tap ? 1 : 0 });
  };

  // Joystick stick displacement for the positioning game (CONTENT1).
  const sendIconSpeed = (x, y) => {
    const s = socketRef.current;
    if (!s) return;
    let uid;
    try { uid = localStorage.getItem("museumx_user_id"); } catch {}
    if (!uid) return;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    s.emit("icon_speed", { userId: uid, x, y });
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        sessionStatus,
        sessionTerminated,
        bootstrapping,
        userInfo,
        team,
        score,
        rank,
        combo,
        registerUser,
        sendTap,
        sendIconSpeed,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
